<template>
  <ModuleBase
    gray
    :navItems="links"
    :keepAliveComponentName="$route.name === 'customers-all' ? 'CustomersOverview' : undefined"
  />
</template>

<script lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { useSessionStorage } from '@vueuse/core';
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { computed, defineComponent } from 'vue';
import { orderKey } from '@/helpers/compliant-register';
import ModuleBase from '@/modules/ModuleBase.vue';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';

export default defineComponent({
  name: 'Customers',
  components: {
    ModuleBase,
  },
  setup() {
    const { result } = useQuery(
      gql`
        query getCurrentUser {
          currentUser {
            company {
              hasFillableForms
            }
          }
        }
      `,
      null,
      {
        fetchPolicy: 'cache-first',
      },
    );

    const hasFillableForms = computed(
      () => !!result.value?.currentUser.company.hasFillableForms,
    );

    return {
      hasFillableForms,
      orderKey,
    };
  },
  computed: {
    ...mapState(useUserStore, ['hasFeatureFlag']),
    showPrepaidCards() {
      return (
        this.hasFeatureFlag('module-register') &&
        !(
          unleash.isEnabled('EnableFiskalySign') &&
          this.hasFeatureFlag('fiskaly-sign')
        )
      );
    },
    links() {
      if (
        unleash.isEnabled('CustomerSegments') &&
        (this.$route.name === 'customers-all' ||
          this.$route.name === 'customers-segments' ||
          this.$route.name === 'customers-new')
      ) {
        return [
          {
            text: this.$t('global.pages.customers_list'),
            name: 'customers-all',
          },
          {
            text: this.$t('global.pages.customers_segments'),
            name: 'customers-segments',
            newUntil: '2025-11-01',
          },
        ];
      } else if (
        !this.$route.matched.find((route) => route.name === 'customer')
      ) {
        return [];
      }

      const links = [
        {
          text: this.$t('global.pages.overview'),
          name: 'customer-overview',
        },
      ];

      const sales = {
        text: this.$t(`global.pages.${orderKey('plural')}`),
        name: 'customer-sales',
      };

      const appointments = {
        text: this.$t('global.pages.appointments'),
        name: 'customer-appointments',
      };

      const reports = {
        text: this.$t('global.pages.reports'),
        name: 'customer-reports',
      };

      const forms = {
        text: this.$t('global.pages.forms'),
        name: 'customer-forms',
      };

      const history = {
        text: this.$t('global.pages.history'),
        name: 'customer-history',
      };

      const loyaltyPoints = {
        text: this.$t('global.pages.loyalty_points'),
        name: 'customer-loyalty-points',
      };

      const ipl = {
        text: this.$t('global.pages.ipl'),
        name: 'customer-ipl',
      };

      const prepaidCards = {
        text: this.$t('global.pages.prepaid_cards'),
        name: 'customer-prepaid-cards',
      };

      const feedbackEnabled = {
        text: this.$t('global.pages.feedback'),
        name: 'customer-feedback',
      };

      if (this.hasFeatureFlag('module-register')) {
        links.push(sales);
      }

      if (this.hasFeatureFlag('module-appointments')) {
        links.push(appointments);
      }

      if (this.hasFeatureFlag('appointment-reports')) {
        links.push(reports);
      }

      if (this.hasFeatureFlag('customer-forms') && this.hasFillableForms) {
        links.push(forms);
      }

      if (this.hasFeatureFlag('module-register')) {
        links.push(history);
      }

      if (
        this.hasFeatureFlag('module-register') &&
        this.hasFeatureFlag('customer-loyalty-points')
      ) {
        links.push(loyaltyPoints);
      }

      if (this.hasFeatureFlag('customer-ipl')) {
        links.push(ipl);
      }

      if (this.showPrepaidCards) {
        links.push(prepaidCards);
      }

      if (this.hasFeatureFlag('feedback-enabled')) {
        links.push(feedbackEnabled);
      }

      return links;
    },
  },
  unmounted() {
    const customerSegmentReferrer = useSessionStorage(
      'customer-segment-referrer',
      null,
    );
    if (customerSegmentReferrer.value) {
      customerSegmentReferrer.value = null;
    }
  },
});
</script>
